import { AxiosError } from 'axios'
import { axiosInstance } from '@methods/axiosConfig'
import { API_URL, DATA_COLLECTION_SERVICE_PATH } from '@constants/apiUrl'
import { forceRedirect } from '@methods/forceRedirect'
import { DATA_COLLECTION_ERROR_CODES } from '@/types/dataCollection'
import type { Base64File } from '@/components/Generic'
import type {
  ConfirmDocumentUploadResponse,
  DataCollectionErrorCode,
  DataCollectionErrorResponse,
  DeleteDocumentResponse,
  DocumentRequest,
  GetContextResponse,
  RequestDocumentUploadResponse,
  SubmitAnswersRequest,
  SubmitAnswersResponse,
  UploadDocumentResponse,
  Answers,
} from '@/types/dataCollection'

const BASE = `${API_URL}/${DATA_COLLECTION_SERVICE_PATH}`

/**
 * Files larger than this go through the presigned S3 path; smaller ones keep
 * using the inline base64 POST. Kept comfortably under the API Gateway base64
 * limit (~4.5 MB of real content after base64 inflation).
 */
export const PRESIGNED_UPLOAD_THRESHOLD_MB = 4

const EXTENSION_CONTENT_TYPES: Record<string, string> = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
}

/**
 * Content type for the presigned request. Prefers the browser-provided
 * `file.type`, falling back to the file extension when it is empty.
 */
function resolveContentType(file: Base64File): string {
  if (file.type) return file.type
  const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
  return EXTENSION_CONTENT_TYPES[ext] ?? ''
}

/** Reconstruct a Blob from a `data:...;base64,...` URI (fallback when the raw File is unavailable). */
function dataUriToBlob(dataUri: string): Blob {
  const [header, base64 = ''] = dataUri.split(',', 2)
  const contentType = header.match(/data:([^;]+)/)?.[1] ?? 'application/octet-stream'
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return new Blob([bytes], { type: contentType })
}

export async function getContext(): Promise<GetContextResponse> {
  const { data } = await axiosInstance.get<GetContextResponse>(`${BASE}/get-context`, {
    withCredentials: true,
  })
  return data
}

export async function submitAnswers(
  screenId: string,
  answers: Answers,
  returnToScreenId?: string
): Promise<SubmitAnswersResponse> {
  const body: SubmitAnswersRequest = { screenId, answers }
  if (returnToScreenId !== undefined) body.returnToScreenId = returnToScreenId
  const { data } = await axiosInstance.post<SubmitAnswersResponse>(`${BASE}/submit-answers`, body, {
    withCredentials: true,
  })
  return data
}

export async function uploadDocument(
  documentRequestId: string,
  file: string,
  fileName: string
): Promise<UploadDocumentResponse> {
  const { data } = await axiosInstance.post<UploadDocumentResponse>(
    `${BASE}/upload-document`,
    { documentRequestId, file, fileName },
    { withCredentials: true }
  )
  return data
}

/** Thrown when S3 rejects the direct upload (bad policy, wrong type, too large, or expired TTL). */
export class DocumentUploadRejectedError extends Error {
  constructor(readonly status: number) {
    super(`S3 rejected the upload (status ${status})`)
    this.name = 'DocumentUploadRejectedError'
  }
}

// --- Presigned (large-file) upload: request slot → POST to S3 → confirm ---

async function requestDocumentUpload(
  documentRequestId: string,
  contentType: string
): Promise<RequestDocumentUploadResponse> {
  const { data } = await axiosInstance.post<RequestDocumentUploadResponse>(
    `${BASE}/request-document-upload`,
    { documentRequestId, contentType },
    { withCredentials: true }
  )
  return data
}

async function uploadFileToS3(
  url: string,
  fields: Record<string, string>,
  body: Blob | File
): Promise<void> {
  const form = new FormData()
  // Every policy field first, then the file last — S3 ignores anything after the file part.
  Object.entries(fields).forEach(([key, value]) => form.append(key, value))
  form.append('file', body)

  // Cross-origin request to S3: no credentials, and no explicit Content-Type header
  // (the browser must set the multipart boundary itself).
  const res = await fetch(url, { method: 'POST', body: form })
  if (res.status !== 204) throw new DocumentUploadRejectedError(res.status)
}

async function confirmDocumentUpload(
  documentRequestId: string,
  documentId: string,
  fileName: string
): Promise<ConfirmDocumentUploadResponse> {
  const { data } = await axiosInstance.post<ConfirmDocumentUploadResponse>(
    `${BASE}/confirm-document-upload`,
    { documentRequestId, documentId, fileName },
    { withCredentials: true }
  )
  return data
}

/**
 * Upload a single document, choosing the transport by size: small files ride
 * inline as base64 (one call); large files go straight to S3 via the presigned
 * flow. Both resolve to the same response shape, so callers render identically.
 */
export async function uploadDocumentFile(
  request: DocumentRequest,
  file: Base64File
): Promise<UploadDocumentResponse> {
  if (file.sizeMB <= PRESIGNED_UPLOAD_THRESHOLD_MB) {
    return uploadDocument(request.id, file.base64, file.name)
  }

  const contentType = resolveContentType(file)
  const { documentId, url, fields } = await requestDocumentUpload(request.id, contentType)
  await uploadFileToS3(url, fields, file.file ?? dataUriToBlob(file.base64))
  return confirmDocumentUpload(request.id, documentId, file.name)
}

export async function deleteDocument(documentId: string): Promise<DeleteDocumentResponse> {
  const { data } = await axiosInstance.post<DeleteDocumentResponse>(
    `${BASE}/delete-document`,
    { documentId },
    { withCredentials: true }
  )
  return data
}

export function navigateToComplete(): void {
  forceRedirect(`${BASE}/complete`)
}

export function getDataCollectionErrorCode(error: unknown): DataCollectionErrorCode | undefined {
  const response = (error as AxiosError<DataCollectionErrorResponse>)?.response
  const code = response?.data?.error
  return isDataCollectionErrorCode(code) ? code : undefined
}

/**
 * The transaction can no longer be edited: it is already completed, or frozen while it waits
 * on malware scans (`TransactionNotEditable`). Both mean the same to the UI — the flow is over
 * from the user's side, so send them on via /complete instead of offering a retry.
 */
export function isTerminalTransactionError(error: unknown): boolean {
  const code = getDataCollectionErrorCode(error)
  return (
    code === 'DataCollection.TransactionAlreadyCompleted' ||
    code === 'DataCollection.TransactionNotEditable'
  )
}

function isDataCollectionErrorCode(value: unknown): value is DataCollectionErrorCode {
  return (DATA_COLLECTION_ERROR_CODES as readonly string[]).includes(value as string)
}
